/**
 * CloudySky Layout Module
 * Provides functionality for the CloudySky weather app layout
 */

import { initMap } from './map.js';
import { showContentModal } from './modal-module.js';

export function initCloudyLayout() {
  createLayoutElements();
  initSidebarToggle();
  initHeaderNavigation();
  initMapContainer();
  initMobileDrawer();
  addLayoutEventListeners();
}

function createLayoutElements() {
  const layoutContainer = document.createElement('div');
  layoutContainer.className = 'cloudy-layout';

  const header = createHeader();
  const content = createContent();

  layoutContainer.appendChild(header);
  layoutContainer.appendChild(content);

  // Save existing elements
  const existingElements = preserveExistingElements();

  // Replace body content
  document.body.innerHTML = '';
  document.body.appendChild(layoutContainer);

  // Restore preserved elements
  restoreExistingElements(existingElements);
}

function createHeader() {
  const header = document.createElement('div');
  header.className = 'cloudy-header';

  // Brand logo
  const brand = document.createElement('div');
  brand.className = 'header-brand';
  brand.innerHTML = '☁️ <span>Cloudy<span class="brand-sky">Sky</span></span>';

  // Navigation
  const nav = createNavigation();

  // Actions (search, theme toggle, menu)
  const actions = createHeaderActions();

  header.appendChild(brand);
  header.appendChild(nav);
  header.appendChild(actions);

  return header;
}

function createNavigation() {
  const nav = document.createElement('nav');
  nav.className = 'header-nav';

  const navItems = [
    { id: 'radar', text: 'Radar', active: true },
    { id: 'meteorologist', text: 'Meteorologist' },
    { id: 'cameras', text: 'Cameras' },
  ];

  navItems.forEach(item => {
    const navItem = document.createElement('a');
    navItem.className = `header-nav-item ${item.active ? 'active' : ''}`;
    navItem.dataset.view = item.id;
    navItem.textContent = item.text;
    nav.appendChild(navItem);
  });

  return nav;
}

function createHeaderActions() {
  const actions = document.createElement('div');
  actions.className = 'header-actions';

  // Search button
  const searchBtn = document.createElement('button');
  searchBtn.className = 'header-button';
  searchBtn.innerHTML = '🔍';
  searchBtn.setAttribute('aria-label', 'Search');

  // Theme toggle
  const themeBtn = document.createElement('button');
  themeBtn.className = 'header-button';
  themeBtn.id = 'dark-mode-toggle';
  themeBtn.innerHTML = '🌙';
  themeBtn.setAttribute('aria-label', 'Toggle dark mode');

  // Menu toggle (for desktop sidebar)
  const menuBtn = document.createElement('button');
  menuBtn.className = 'header-button sidebar-toggle';
  menuBtn.innerHTML = '☰';
  menuBtn.setAttribute('aria-label', 'Toggle menu');
  menuBtn.setAttribute('data-toggle', 'sidebar');

  // Mobile menu toggle (for mobile drawer)
  const mobileMenuBtn = document.createElement('button');
  mobileMenuBtn.className = 'header-button mobile-toggle';
  mobileMenuBtn.innerHTML = '☰';
  mobileMenuBtn.setAttribute('aria-label', 'Toggle mobile menu');
  mobileMenuBtn.setAttribute('data-toggle', 'mobile');

  actions.appendChild(searchBtn);
  actions.appendChild(themeBtn);
  actions.appendChild(menuBtn);
  actions.appendChild(mobileMenuBtn);

  return actions;
}

function createContent() {
  const content = document.createElement('div');
  content.className = 'cloudy-content';

  const sidebar = createSidebar();
  const mapContainer = createMapContainer();
  const mobileDrawer = createMobileDrawer();

  content.appendChild(sidebar);
  content.appendChild(mapContainer);
  content.appendChild(mobileDrawer);

  return content;
}

function createSidebar() {
  const sidebar = document.createElement('div');
  sidebar.className = 'cloudy-sidebar';

  const menu = document.createElement('div');
  menu.className = 'sidebar-menu';

  const menuItems = [
    { id: 'screenshot', icon: '📷', text: 'Screenshot' },
    { id: 'record', icon: '🎥', text: 'Record MP4' },
    { id: 'share', icon: '🔗', text: 'Share Map' },
    { id: 'cameras', icon: '🎥', text: 'Cameras' },
    { id: 'settings', icon: '⚙️', text: 'Settings' },
  ];

  menuItems.forEach(item => {
    const menuItem = document.createElement('div');
    menuItem.className = 'sidebar-menu-item';
    menuItem.dataset.action = item.id;

    const icon = document.createElement('span');
    icon.textContent = item.icon;

    const text = document.createElement('span');
    text.textContent = item.text;

    menuItem.appendChild(icon);
    menuItem.appendChild(text);
    menu.appendChild(menuItem);
  });

  sidebar.appendChild(menu);
  return sidebar;
}

function createMapContainer() {
  const mapContainer = document.createElement('div');
  mapContainer.className = 'cloudy-map-container';
  mapContainer.id = 'map-container';

  // Ensure the container takes full height
  mapContainer.style.display = 'flex';
  mapContainer.style.flexDirection = 'column';

  return mapContainer;
}

function createMobileDrawer() {
  const drawer = document.createElement('div');
  drawer.className = 'mobile-drawer';

  const handle = document.createElement('div');
  handle.className = 'drawer-handle';

  const content = document.createElement('div');
  content.className = 'drawer-content';
  
  // Add mobile navigation items - same as header nav
  const mobileNav = document.createElement('div');
  mobileNav.className = 'mobile-nav';
  
  const navItems = [
    { id: 'radar', text: 'Radar', active: true },
    { id: 'meteorologist', text: 'Meteorologist' },
    { id: 'cameras', text: 'Cameras' },
  ];

  navItems.forEach(item => {
    const navItem = document.createElement('a');
    navItem.className = `mobile-nav-item ${item.active ? 'active' : ''}`;
    navItem.dataset.view = item.id;
    navItem.textContent = item.text;
    mobileNav.appendChild(navItem);
  });
  
  content.appendChild(mobileNav);
  drawer.appendChild(handle);
  drawer.appendChild(content);

  return drawer;
}

function initSidebarToggle() {
  const sidebarToggleBtn = document.querySelector('.header-button.sidebar-toggle');
  const mobileToggleBtn = document.querySelector('.header-button.mobile-toggle');
  const sidebar = document.querySelector('.cloudy-sidebar');
  const drawer = document.querySelector('.mobile-drawer');

  // Desktop sidebar toggle
  if (sidebarToggleBtn && sidebar) {
    sidebarToggleBtn.addEventListener('click', () => {
      sidebar.classList.toggle('open');
    });
  }

  // Mobile drawer toggle
  if (mobileToggleBtn && drawer) {
    mobileToggleBtn.addEventListener('click', () => {
      drawer.classList.toggle('open');
      console.log('Mobile drawer toggled:', drawer.classList.contains('open'));
    });
  }
}

function initHeaderNavigation() {
  const navItems = document.querySelectorAll('.header-nav-item');

  navItems.forEach(item => {
    item.addEventListener('click', e => {
      navItems.forEach(nav => nav.classList.remove('active'));
      item.classList.add('active');
      handleViewChange(item.dataset.view);
    });
  });
}

function initMobileDrawer() {
  const drawer = document.querySelector('.mobile-drawer');
  const handle = document.querySelector('.drawer-handle');
  const mobileNavItems = document.querySelectorAll('.mobile-nav-item');

  // Add click events to mobile nav items
  mobileNavItems.forEach(item => {
    item.addEventListener('click', e => {
      mobileNavItems.forEach(nav => nav.classList.remove('active'));
      item.classList.add('active');
      handleViewChange(item.dataset.view);
      drawer.classList.remove('open'); // Close drawer after selection
    });
  });

  if (handle && drawer) {
    let startY = 0;
    let currentY = 0;

    handle.addEventListener('touchstart', e => {
      startY = e.touches[0].clientY;
      drawer.style.transition = 'none';
    });

    handle.addEventListener('touchmove', e => {
      currentY = e.touches[0].clientY;
      const diff = currentY - startY;

      if (diff > 0) {
        // Dragging down
        drawer.style.transform = `translateY(${diff}px)`;
      }
    });

    handle.addEventListener('touchend', () => {
      drawer.style.transition = 'transform 0.3s ease';

      if (currentY - startY > 50) {
        drawer.classList.remove('open');
      } else {
        drawer.style.transform = '';
      }
    });
  }
}

function initMapContainer() {
  const mapContainer = document.getElementById('map-container');
  if (mapContainer) {
    initMap(mapContainer);
    // Show default map view (NWS map of the United States)
    showDefaultMapView(mapContainer);
  }
}

function preserveExistingElements() {
  return {
    darkModeToggle: document.getElementById('dark-mode-toggle'),
    alerts: document.getElementById('emergency-alerts'),
    modal: document.getElementById('content-modal'),
  };
}

function restoreExistingElements(elements) {
  if (elements.modal) {
    document.body.appendChild(elements.modal);
  }
  if (elements.alerts) {
    const header = document.querySelector('.cloudy-header');
    if (header) {
      header.appendChild(elements.alerts);
    }
  }
}

function handleViewChange(view) {
  console.log(`Changing view to: ${view}`);

  const mapContainer = document.getElementById('map-container');
  if (!mapContainer) {
    return;
  }

  // Clear current content
  mapContainer.innerHTML = '';

  switch (view) {
    case 'radar':
      showRadarView(mapContainer);
      break;
    case 'meteorologist':
      showMeteorologistView(mapContainer);
      break;
    case 'cameras':
      showCamerasView(mapContainer);
      break;
    default:
      showDefaultMapView(mapContainer);
  }
}

// Function to show radar view
function showRadarView(container) {
  try {
    // Create the settings object with the coordinates for NWS radar
    const settings = {
      agenda: {
        id: null,
        center: [-96.35, 36.91], // Center of US
        location: null,
        zoom: 4,
      },
      animating: true, // Keep animation running
      autoUpdate: true, // Auto update the radar
      loopSpeed: 500, // Animation speed (milliseconds per frame)
      base: 'standard',
      artcc: false,
      county: false,
      cwa: false,
      rfc: false,
      state: false,
      menu: false, // Hide the menu
      shortFusedOnly: false,
      opacity: {
        alerts: 0.8,
        local: 0.6,
        localStations: 0.8,
        national: 0.6,
      },
    };

    // Convert settings to base64 encoded JSON
    const settingsStr = JSON.stringify(settings);
    const encodedSettings = btoa(settingsStr);

    // Create the URL with properly encoded settings
    const nwsRadarUrl = `https://radar.weather.gov/?settings=v1_${encodedSettings}`;
    const title = 'NWS Weather Radar';

    // Create an iframe to embed the radar
    container.innerHTML = `
      <div class="static-map-container">
        <iframe
          src="${nwsRadarUrl}"
          title="${title}"
          frameborder="0"
          allowfullscreen
          loading="lazy"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation"
        ></iframe>
      </div>
    `;
  } catch (error) {
    console.error('Error showing NWS radar:', error);
    container.innerHTML = `<div class="error-container"><div class="error-icon">❌</div><div class="error-content"><p>Unable to load NWS radar. Please try again later.</p></div></div>`;
  }
}

// Function to show satellite view
function showSatelliteView(container) {
  container.innerHTML = `<div class="info-message">Satellite view is not yet implemented.</div>`;
}

// Function to show alerts view
function showAlertsView(container) {
  container.innerHTML = `<div class="info-message">Alerts view is not yet implemented.</div>`;
}

// Function to show cameras view
function showCamerasView(container) {
  // Hardcoded camera data
  const cameraData = {
    states: [
      { stateCode: 'CA', stateName: 'California', cameraLink: 'https://QuickMap.dot.ca.gov' },
      { stateCode: 'TX', stateName: 'Texas', cameraLink: 'https://DriveTexas.org' },
      { stateCode: 'FL', stateName: 'Florida', cameraLink: 'https://FL511.com' },
      { stateCode: 'NY', stateName: 'New York', cameraLink: 'https://511NY.org' },
      { stateCode: 'PA', stateName: 'Pennsylvania', cameraLink: 'https://511PA.com' },
      { stateCode: 'IL', stateName: 'Illinois', cameraLink: 'https://GettingAroundIllinois.com' },
      { stateCode: 'OH', stateName: 'Ohio', cameraLink: 'https://OHGO.com' },
      { stateCode: 'GA', stateName: 'Georgia', cameraLink: 'https://511ga.org' },
      { stateCode: 'NC', stateName: 'North Carolina', cameraLink: 'https://NCDOT.gov' },
      { stateCode: 'MI', stateName: 'Michigan', cameraLink: 'https://www.michigan.gov/mdot' },
      { stateCode: 'NJ', stateName: 'New Jersey', cameraLink: 'https://511nj.org' },
      { stateCode: 'VA', stateName: 'Virginia', cameraLink: 'https://511Virginia.org' },
    ],
  };

  try {
    // Create the cameras view content
    const camerasContent = `
      <div class="cameras-container">
        <h2 class="cameras-title">Traffic Cameras by State</h2>
        <div class="cameras-grid">
          ${cameraData.states
            .map(
              state => `
            <div class="camera-card">
              <div class="camera-card-header">
                <h3>${state.stateName}</h3>
              </div>
              <div class="camera-card-body">
                <div class="camera-info">
                  <p>State Code: ${state.stateCode}</p>
                  <p>Last Updated: ${new Date().toLocaleString()}</p>
                </div>
                <div class="camera-preview">
                  <img src="https://via.placeholder.com/300x200?text=Traffic+Camera+${state.stateCode}" alt="${state.stateName} Traffic Camera" class="camera-image">
                </div>
                <a href="${state.cameraLink}" target="_blank" class="camera-link">View Live Cameras</a>
              </div>
            </div>
          `
            )
            .join('')}
        </div>
      </div>
    `;

    container.innerHTML = camerasContent;
  } catch (error) {
    console.error('Error displaying camera data:', error);
    container.innerHTML = `<div class="error-container"><div class="error-icon">❌</div><div class="error-content"><p>Unable to display camera data. Please try again later.</p></div></div>`;
  }
}

// Function to show meteorologist view
function showMeteorologistView(container) {
  const meteorologistContent = `
    <div class="meteorologist-container">
      <h2 class="meteorologist-title">Weather Experts on YouTube</h2>
      <div class="meteorologist-grid">
        <div class="meteorologist-card">
          <div class="meteorologist-card-header">
            <h3>Ryan Hall Y'all</h3>
          </div>
          <div class="meteorologist-card-body">
            <p>Renowned for his live storm tracking and severe weather coverage. Ryan provides real-time analysis during major weather events across the United States.</p>
            <a href="https://www.youtube.com/@RyanHallYall" target="_blank" class="meteorologist-link">Visit Channel</a>
          </div>
        </div>

        <div class="meteorologist-card">
          <div class="meteorologist-card-header">
            <h3>Max Velocity</h3>
          </div>
          <div class="meteorologist-card-body">
            <p>Specializes in hurricane tracking and forecasting. Provides detailed analysis of tropical systems and their potential impacts.</p>
            <a href="https://www.youtube.com/@MaxVelocityWX" target="_blank" class="meteorologist-link">Visit Channel</a>
          </div>
        </div>

        <div class="meteorologist-card">
          <div class="meteorologist-card-header">
            <h3>Y'all Bot</h3>
          </div>
          <div class="meteorologist-card-body">
            <p>An AI-powered weather channel that provides automated weather updates and forecasts. Combines data from multiple sources for comprehensive coverage.</p>
            <a href="https://www.youtube.com/@OfficialYallBot" target="_blank" class="meteorologist-link">Visit Channel</a>
          </div>
        </div>

        <div class="meteorologist-card">
          <div class="meteorologist-card-header">
            <h3>TRD Pro Storm Chasers</h3>
          </div>
          <div class="meteorologist-card-body">
            <p>Professional storm chasers providing live footage and analysis of tornadoes, severe thunderstorms, and other extreme weather events.</p>
            <a href="https://www.youtube.com/@trdprostormchasers" target="_blank" class="meteorologist-link">Visit Channel</a>
          </div>
        </div>

        <div class="meteorologist-card">
          <div class="meteorologist-card-header">
            <h3>Andy Hill</h3>
          </div>
          <div class="meteorologist-card-body">
            <p>Meteorologist providing detailed weather forecasts and educational content about weather phenomena and climate patterns.</p>
            <a href="https://www.youtube.com/@metandyhill" target="_blank" class="meteorologist-link">Visit Channel</a>
          </div>
        </div>
      </div>
    </div>
  `;

  container.innerHTML = meteorologistContent;
}

// Function to show default map view
function showDefaultMapView(container) {
  try {
    // Create the settings object with the coordinates for NWS map
    const settings = {
      agenda: {
        id: null,
        center: [-96.35, 36.91], // Center of US
        location: null,
        zoom: 4,
      },
      animating: true, // Enable animation for the default view too
      autoUpdate: true, // Auto update the radar
      loopSpeed: 500, // Animation speed (milliseconds per frame)
      base: 'standard',
      artcc: false,
      county: false,
      cwa: false,
      rfc: false,
      state: true,
      menu: false, // Hide the menu
      shortFusedOnly: false,
      opacity: {
        alerts: 0.8,
        local: 0.6,
        localStations: 0.8,
        national: 0.6,
      },
    };

    // Convert settings to base64 encoded JSON
    const settingsStr = JSON.stringify(settings);
    const encodedSettings = btoa(settingsStr);

    // Create the URL with properly encoded settings
    const nwsMapUrl = `https://radar.weather.gov/?settings=v1_${encodedSettings}`;
    const title = 'NWS Weather Map';

    // Create an iframe to embed the map
    container.innerHTML = `
      <div class="static-map-container">
        <iframe
          src="${nwsMapUrl}"
          title="${title}"
          frameborder="0"
          allowfullscreen
          loading="lazy"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-top-navigation"
        ></iframe>
      </div>
    `;
  } catch (error) {
    console.error('Error showing NWS map:', error);
    container.innerHTML = `<div class="error-container"><div class="error-icon">❌</div><div class="error-content"><p>Unable to load NWS map. Please try again later.</p></div></div>`;
  }
}

function addLayoutEventListeners() {
  // Add click handlers for menu items
  document.querySelectorAll('.sidebar-menu-item').forEach(item => {
    item.addEventListener('click', () => {
      handleMenuAction(item.dataset.action);
    });
  });
}

function handleMenuAction(action) {
  switch (action) {
    case 'screenshot':
      // Implement screenshot
      break;
    case 'record':
      // Implement recording
      break;
    case 'share':
      // Implement sharing
      break;
    case 'cameras': {
      // Select the cameras nav item to show the cameras view
      const camerasNavItem = document.querySelector('.header-nav-item[data-view="cameras"]');
      if (camerasNavItem) {
        camerasNavItem.click();
      }
      break;
    }
    case 'settings':
      showContentModal('settings');
      break;
  }
}
